import { lazy, Suspense, useEffect, useState } from "react";
import {
  BarChart3, Boxes, FlaskConical, ChefHat, ClipboardList, Clock3, Eye, Gift, HandCoins, KeyRound, LayoutDashboard, LayoutGrid, Loader2, LogOut, Menu as MenuIcon, Printer,
  ReceiptText, ScrollText, Settings as SettingsIcon, ShieldCheck, ShoppingBag, Sparkles, Tag, UserCheck, Users, UtensilsCrossed, Wifi, WifiOff, type LucideIcon
} from "lucide-react";
import { DAY_LIMITED_ROLES, MFA_ROLES, ROLE_LABELS, type Permission } from "@mango-moons/domain";
import { navigate, signOut, toast, useApp, useCan, useData, useSettings, type Screen } from "./state";
import logo from "./assets/logo.png";
import { desktop } from "./lib/desktop";
import { DEMO, leaveDemo, openDemo } from "./lib/demoGuard";
import { cx, time } from "./lib/format";
import { Modal, Toasts } from "./lib/ui";
import { nativeApp } from "./lib/androidApp";
import { ChangePassword, Login, Setup, TotpSetup } from "./screens/Auth";
import { OrderDetail } from "./screens/OrderDetail";
import { PublicStatus } from "./screens/PublicStatus";
import { OnlineOrdersSwitch, useOnlineOrders } from "./lib/onlineOrders";
import { BellButton, LiveCallsPanel } from "./lib/tableBell";
import { LowStockAlert } from "./lib/lowStock";

const screens = {
  dashboard: lazy(() => import("./screens/Dashboard")), overview: lazy(() => import("./screens/Overview")), sale: lazy(() => import("./screens/Sale")), orders: lazy(() => import("./screens/Orders")),
  tables: lazy(() => import("./screens/Tables")), kitchen: lazy(() => import("./screens/Kitchen")), menu: lazy(() => import("./screens/Menu")),
  promotions: lazy(() => import("./screens/Promotions")), complimentary: lazy(() => import("./screens/Complimentary")), customers: lazy(() => import("./screens/Customers")), inventory: lazy(() => import("./screens/Inventory")),
  expenses: lazy(() => import("./screens/Expenses")), shifts: lazy(() => import("./screens/Shifts")), reports: lazy(() => import("./screens/Reports")),
  staff: lazy(() => import("./screens/Staff")), printers: lazy(() => import("./screens/Printers")), updates: lazy(() => import("./screens/Updates")),
  settings: lazy(() => import("./screens/Settings")), audit: lazy(() => import("./screens/Audit")), attendance: lazy(() => import("./screens/Attendance"))
} satisfies Record<Screen, unknown>;

type NavItem = { id: Screen; label: string; icon: LucideIcon; needs?: Permission; key?: string };
const NAVIGATION: { group: string; items: NavItem[] }[] = [
  { group: "Service", items: [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, needs: "orders.read", key: "F1" },
    { id: "overview", label: "Management View", icon: Eye, needs: "reports.read" },
    { id: "sale", label: "New Sale", icon: ShoppingBag, needs: "orders.create", key: "F2" },
    { id: "orders", label: "Orders", icon: ClipboardList, needs: "orders.read", key: "F3" },
    { id: "tables", label: "Tables", icon: LayoutGrid, needs: "orders.read", key: "F4" },
    { id: "kitchen", label: "Kitchen", icon: ChefHat, needs: "kitchen.read", key: "F5" },
    { id: "attendance", label: "Attendance", icon: UserCheck, needs: "attendance.clock" }
  ] },
  { group: "Manage", items: [
    { id: "menu", label: "Menu", icon: UtensilsCrossed, needs: "menu.manage" },
    { id: "promotions", label: "Promotions", icon: Tag, needs: "promotions.manage" },
    { id: "complimentary", label: "Complimentary", icon: Gift, needs: "reports.read" },
    { id: "customers", label: "Customers", icon: Users, needs: "customers.read" },
    { id: "inventory", label: "Inventory", icon: Boxes, needs: "inventory.manage" },
    { id: "expenses", label: "Expenses", icon: ReceiptText, needs: "expenses.manage" },
    { id: "shifts", label: "Cash & Shifts", icon: HandCoins, needs: "shifts.manage" },
    { id: "reports", label: "Reports", icon: BarChart3, needs: "reports.read" }
  ] },
  { group: "System", items: [
    { id: "staff", label: "Staff & Access", icon: ShieldCheck, needs: "staff.manage" },
    { id: "printers", label: "Printers", icon: Printer },
    { id: "updates", label: "Update Center", icon: Sparkles, needs: "updates.manage" },
    { id: "settings", label: "Settings", icon: SettingsIcon },
    { id: "audit", label: "Audit Log", icon: ScrollText, needs: "audit.read" }
  ] }
];

/**
 * Printers, Settings and the audit log are for the Super Admin on a POS computer only: never in the managers' phone
 * app, and not for managers or cashiers on the POS. (Everyone can still change their own password from the user card.)
 */
const ADMIN_DESK_ONLY = new Set<Screen>(["printers", "settings", "audit"]);

const Splash = ({ children }: { children?: React.ReactNode }) => <div className="grid h-full place-items-center p-6 text-center">{children ?? <Loader2 className="animate-spin text-mango" size={30}/>}</div>;

/**
 * Shown when Firestore stops answering because the free plan's daily reads are used up, so the POS does not just
 * look empty. The allowance comes back by itself when Google's day turns (midnight US Pacific time).
 */
function QuotaBanner() {
  const [hit, setHit] = useState(false);
  useEffect(() => { const on = () => setHit(true); addEventListener("mm-firestore-quota", on); return () => removeEventListener("mm-firestore-quota", on); }, []);
  if (!hit) return null;
  return <div role="alert" className="no-print border-b border-bad/50 bg-bad/15 px-4 py-2.5 text-sm text-cream">
    <strong className="text-bad">Today's free database limit is used up.</strong> New data cannot load until it resets, around 12:30–1:30 pm Sri Lanka time. Orders already on screen stay; reload the POS after the reset.
  </div>;
}

export function App() {
  const status = useApp((state) => state.status);
  const error = useApp((state) => state.error);
  const user = useApp((state) => state.user);
  const mode = useApp((state) => state.mode);
  const publicToken = /^#\/status\/([0-9a-f]{40})$/.exec(location.hash)?.[1];
  if (publicToken) return <PublicStatus token={publicToken}/>;
  return <>
    {status === "loading" ? <Splash/>
      : status === "failed" ? <Splash><div><h1 className="mb-2 text-2xl">Mango Moons POS could not start</h1><p className="text-mute">{error}</p></div></Splash>
      : status === "setup" ? <Setup/>
      : status === "signed-out" || !user ? <Login/>
      : user.mustChangePassword ? <ChangePassword forced/>
      // Management's cloud sign-in needs the authenticator app; it is set up before anything else.
      : mode === "firebase" && MFA_ROLES.includes(user.role) && !user.hasTotp ? <TotpSetup forced/>
      : <Shell/>}
    <Toasts/>
  </>;
}

function Shell() {
  const screen = useApp((state) => state.screen);
  const user = useApp((state) => state.user)!;
  const online = useApp((state) => state.online);
  const mode = useApp((state) => state.mode);
  const openOrderId = useApp((state) => state.openOrderId);
  const settings = useSettings();
  useOnlineOrders();
  const shift = useData("shifts").find((entry) => entry.status === "open");
  const can = useCan();
  const [navOpen, setNavOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [clock, setClock] = useState(() => new Date().toISOString());
  const autoLockMinutes = settings.autoLockMinutes ?? 15;
  const viewOnly = !can("orders.create") && !can("kitchen.update");

  // An unattended terminal signs itself out, so a passer-by cannot sell, refund or read reports as the last user.
  useEffect(() => {
    if (!autoLockMinutes || viewOnly) return;
    let last = Date.now();
    const touch = () => { last = Date.now(); };
    const events = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;
    events.forEach((name) => addEventListener(name, touch, { passive: true }));
    const timer = setInterval(() => {
      if (Date.now() - last < autoLockMinutes * 60_000) return;
      toast("info", `Signed out after ${autoLockMinutes} minutes without activity.`);
      void signOut();
    }, 15_000);
    return () => { clearInterval(timer); events.forEach((name) => removeEventListener(name, touch)); };
  }, [autoLockMinutes, viewOnly]);
  const visible = NAVIGATION.map((group) => ({ ...group, items: group.items.filter((item) => (!item.needs || can(item.needs)) && (item.id !== "kitchen" || settings.kitchenDisplayEnabled)
    && (!ADMIN_DESK_ONLY.has(item.id) || (!nativeApp && user.role === "super_admin"))) })).filter((group) => group.items.length > 0);
  const allowed = visible.flatMap((group) => group.items);
  const current = allowed.find((item) => item.id === screen) ?? allowed[0]!;
  const Active = screens[current.id];

  useEffect(() => { const timer = setInterval(() => setClock(new Date().toISOString()), 20_000); return () => clearInterval(timer); }, []);
  useEffect(() => {
    // Function keys belong to the browser (F5 reloads) on the web; only the desktop app claims them.
    if (!desktop) return;
    const onKey = (event: KeyboardEvent) => {
      const item = allowed.find((entry) => entry.key === event.key);
      if (item && !document.querySelector("dialog[open]")) { event.preventDefault(); navigate(item.id); }
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  });

  return <div className="flex h-full">
    <aside className={cx("no-print fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-gold/15 bg-gradient-to-b from-[#1a140e] via-[#12100e] to-ink backdrop-blur transition-transform lg:static lg:translate-x-0", navOpen ? "translate-x-0" : "-translate-x-full")}>
      <div className="flex items-center gap-3 px-5 pb-4 pt-5">
        <img src={logo} alt="" className="size-12 rounded-full shadow-[0_8px_26px_-8px_rgb(255_157_47/0.7)] ring-1 ring-gold/40"/>
        <div className="leading-tight"><strong className="block font-display text-lg">Mango Moons</strong><span className="text-[11px] uppercase tracking-[0.2em] text-gold">Restaurant POS</span></div>
      </div>
      <div className="gold-rule mx-5 mb-3"/>
      <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-4" aria-label="Main">
        {visible.map((group) => <div key={group.group}>
          <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-faint">{group.group}</p>
          {group.items.map(({ id, label, icon: Icon, key }) => <button key={id} onClick={() => { navigate(id); setNavOpen(false); }} aria-current={current.id === id ? "page" : undefined}
            className={cx("group flex h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-medium transition-colors", current.id === id ? "relative bg-gradient-to-r from-gold/20 via-mango/10 to-transparent text-cream before:absolute before:inset-y-2 before:left-0 before:w-1 before:rounded-full before:bg-gradient-to-b before:from-gold before:to-mango" : "text-mute hover:bg-raised/70 hover:text-cream")}>
            <Icon size={18} className={current.id === id ? "text-mango" : ""}/><span className="flex-1">{label}</span>
            {key && desktop && <kbd className="rounded border border-line px-1 text-[10px] text-faint">{key}</kbd>}
          </button>)}
        </div>)}
      </nav>
      {!DEMO && !DAY_LIMITED_ROLES.includes(user.role) && <button onClick={openDemo} className="mx-3 flex items-center gap-2.5 rounded-xl border border-gold/40 bg-gold/5 px-3 py-2.5 text-left text-sm font-medium text-gold hover:bg-gold/10">
        <FlaskConical size={17}/><span className="flex-1">Try the demo<small className="block text-[11px] font-normal text-mute">Sample restaurant · nothing saved</small></span></button>}
      <div className="m-3 flex items-center gap-3 rounded-2xl border border-line bg-panel p-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-gold/15 text-sm font-bold text-gold">{user.displayName.split(/\s+/).map((part) => part[0]).slice(0, 2).join("").toUpperCase()}</span>
        <span className="min-w-0 flex-1 leading-tight"><strong className="block truncate text-sm">{user.displayName}</strong><small className="text-xs text-mute">{ROLE_LABELS[user.role]}</small></span>
        {!allowed.some((item) => item.id === "settings") && <button onClick={() => setPasswordOpen(true)} aria-label="Change my password" title="Change my password" className="grid size-9 place-items-center rounded-lg text-mute hover:bg-raised hover:text-cream"><KeyRound size={17}/></button>}
        <button onClick={() => void signOut()} aria-label="Sign out" title="Sign out" className="grid size-9 place-items-center rounded-lg text-mute hover:bg-raised hover:text-bad"><LogOut size={17}/></button>
      </div>
    </aside>
    {passwordOpen && <Modal open onClose={() => setPasswordOpen(false)} size="sm" eyebrow="My account" title="Change password"><ChangePassword onDone={() => setPasswordOpen(false)}/></Modal>}
    {navOpen && <button aria-label="Close navigation" className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setNavOpen(false)}/>}
    <div className="flex min-w-0 flex-1 flex-col">
      <header className="no-print flex h-14 shrink-0 items-center gap-3 border-b border-gold/15 bg-[#12100e]/80 px-4 backdrop-blur">
        <button className="grid size-10 place-items-center rounded-lg text-mute hover:bg-raised lg:hidden" onClick={() => setNavOpen(true)} aria-label="Open navigation"><MenuIcon size={20}/></button>
        <div className="min-w-0 flex-1 leading-tight"><strong className="block truncate text-sm">{settings.name}</strong>
          <small className={cx("flex items-center gap-1.5 text-xs", mode === "local" || online ? "text-mute" : "text-bad")}>
            {DEMO ? <><span className="size-1.5 rounded-full bg-gold"/> Demo · not saved</> : mode === "local" ? <><span className="size-1.5 rounded-full bg-ok"/> Saved on this device</> : online ? <><Wifi size={12} className="text-ok"/> Online · live sync</> : <><WifiOff size={12}/> Offline — new orders and payments are paused until the connection returns</>}
          </small>
        </div>
        <LowStockAlert/>
        <BellButton/>
        <OnlineOrdersSwitch/>
        {can("shifts.manage") && <button onClick={() => navigate("shifts")} className={cx("hidden items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold sm:flex", shift ? "border-ok/40 bg-ok/10 text-ok" : "border-bad/40 bg-bad/10 text-bad")}>
          <span className={cx("size-1.5 rounded-full", shift ? "bg-ok" : "bg-bad")}/>{shift ? `Shift open · ${time(shift.openedAt)}` : "No shift open"}
        </button>}
        <div className="flex items-center gap-2 rounded-full border border-line px-3 py-1 text-sm tabular-nums text-mute"><Clock3 size={14}/>{time(clock)}</div>
      </header>
      <QuotaBanner/>
      {DEMO && <div role="note" className="no-print flex flex-wrap items-center justify-between gap-3 border-b border-gold/50 bg-[#2a2010] px-4 py-2 text-sm">
        <span><strong className="text-gold">DEMO</strong> · sample restaurant in this window only. Nothing is saved, printed or sent to the cloud; real orders cannot be taken here.</span>
        <button onClick={leaveDemo} className="rounded-lg border border-gold/50 px-3 py-1 text-xs font-semibold text-gold hover:bg-gold/10">Leave the demo</button></div>}
      <main className="min-h-0 flex-1 overflow-y-auto"><Suspense fallback={<Splash/>}><Active/></Suspense></main>
    </div>
    {openOrderId && <OrderDetail orderId={openOrderId}/>}
    <LiveCallsPanel/>
  </div>;
}
